// cadSiteAdapter.ts — Transform cv-cad site-named-export → cv-web SiteData
//
// cv-cad cvxpsite.lsp exports site-level structural grid data:
//   - masterSiteSettings: global site parameters
//   - gridXNumeric: X-axis grid line positions (bay widths in inches)
//   - gridYLetter: Y-axis grid line positions (bay depths in inches)
//   - wallLines: wall segments between grid intersections (panel assignments)
//   - slabLines, slabConnections, tiltColumns, rebarConnections: structural details
//
// Grid values are LISP s-expressions: "((label spacing))" or "((range from to offsets))"
//
// cv-web SiteData is much simpler — just panel placements on a flat grid.
// This adapter extracts grid dimensions and attempts to locate panels from wallLines.
// Structural details (slab, rebar, columns) are not mapped — they produce warnings.

import type { SiteData, SitePanelPlacement } from '../types/panelvar';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CadSiteExport {
  cvVersion:            string;
  type:                 string;
  exportedAt:           string;
  sourceDictionaryKey:  string;
  siteCount:            number;
  sites:                CadSiteEntry[];
  titleBlock:           unknown | null;
}

export interface CadSiteEntry {
  sectionCount:         number;
  drawingName:          string;
  sourceDictionaryKey:  string;
  [key: string]:        unknown;  // gridXNumeric_N, wallLines_N, etc.
}

export interface CadSiteImportResult {
  site:     SiteData;
  warnings: string[];
  gridSummary: {
    xLines: GridLine[];
    yLines: GridLine[];
    wallSegments: WallSegment[];
  };
}

export interface GridLine {
  label: string;
  position: number;  // Cumulative position in inches from origin
}

export interface WallSegment {
  panelRange: string;
  fromGrid:   { x: string; y: string };
  toGrid:     { x: string; y: string };
  offsets:    number[];
  active:     boolean;
}

// ── Detection ──────────────────────────────────────────────────────────────────

export function isCadSiteExport(obj: Record<string, unknown>): boolean {
  return obj['type'] === 'site-named-export'
    && Array.isArray(obj['sites']);
}

// ── LISP s-expression parser ───────────────────────────────────────────────────

/** Parse a LISP s-expression value like "((label value))" or "((range from to ...))" */
function parseLispTuple(sexp: string): string[] {
  // Strip outer parens: "((A 300.0))" → "A 300.0"
  let inner = sexp.trim();
  while (inner.startsWith('(')) inner = inner.slice(1);
  while (inner.endsWith(')'))   inner = inner.slice(0, -1);
  // Split on whitespace, preserving multi-space alignment
  return inner.split(/\s+/).filter(s => s.length > 0);
}

// ── Grid parsing ───────────────────────────────────────────────────────────────

function parseGridLines(
  site: CadSiteEntry,
  prefix: string,
  rowCountKey: string,
): GridLine[] {
  const count = typeof site[rowCountKey] === 'number' ? site[rowCountKey] as number : 0;
  const lines: GridLine[] = [];
  let cumulative = 0;

  for (let i = 1; i <= count; i++) {
    const key = `${prefix}_${i}`;
    const raw = site[key];
    if (typeof raw !== 'string') continue;

    const parts = parseLispTuple(raw);
    if (parts.length < 2) continue;

    const label   = parts[0];
    const spacing = parseFloat(parts[1]);

    // Skip empty rows (label is empty or spacing is 0 with no label)
    if (!label || (label === '' && spacing === 0)) continue;
    if (isNaN(spacing) && label === '0') continue;

    cumulative += isNaN(spacing) ? 0 : spacing;
    lines.push({ label, position: cumulative });
  }

  return lines;
}

// ── Wall line parsing ──────────────────────────────────────────────────────────

function parseWallLines(site: CadSiteEntry): WallSegment[] {
  const count = typeof site['wallLines_rowCount'] === 'number'
    ? site['wallLines_rowCount'] as number
    : 0;

  const segments: WallSegment[] = [];

  for (let i = 1; i <= count; i++) {
    const key = `wallLines_${i}`;
    const raw = site[key];
    if (typeof raw !== 'string') continue;

    const parts = parseLispTuple(raw);
    // Typical: panelRange fromX fromY toX toY offset1 offset2 offset3 offset4 offset5 flag
    // Minimum useful: 5 parts (range + from/to grid refs)
    if (parts.length < 5) continue;

    const panelRange = parts[0];
    // Skip empty/zero rows
    if (panelRange === '' || panelRange === '0') continue;
    // Check if all values are zero (empty slot)
    if (parts.every(p => p === '0' || p === '0.0')) continue;

    const fromX = parts[1];
    const fromY = parts[2];
    const toX   = parts[3];
    const toY   = parts[4];
    const offsets = parts.slice(5, -1).map(Number).filter(n => !isNaN(n));
    const flag  = parts.length > 5 ? parseInt(parts[parts.length - 1], 10) : 1;

    segments.push({
      panelRange,
      fromGrid: { x: fromX, y: fromY },
      toGrid:   { x: toX, y: toY },
      offsets,
      active: flag !== 0,
    });
  }

  return segments;
}

// ── Panel placement extraction ─────────────────────────────────────────────────

function extractPanelPlacements(
  wallSegments: WallSegment[],
  xLines: GridLine[],
  yLines: GridLine[],
): SitePanelPlacement[] {
  const placements: SitePanelPlacement[] = [];
  const xMap = new Map(xLines.map(l => [l.label, l.position]));
  const yMap = new Map(yLines.map(l => [l.label, l.position]));

  for (const seg of wallSegments) {
    if (!seg.active) continue;

    const fromX = xMap.get(seg.fromGrid.x);
    const fromY = yMap.get(seg.fromGrid.y);
    const toX   = xMap.get(seg.toGrid.x);
    const toY   = yMap.get(seg.toGrid.y);

    if (fromX === undefined || fromY === undefined || toX === undefined || toY === undefined) {
      continue;
    }

    // Parse panel range: "1-2" → [1,2], "3" → [3], "41-42" → [41,42]
    const rangeMatch = seg.panelRange.match(/^(\d+)(?:-(\d+))?$/);
    if (!rangeMatch) continue;

    const startPanel = parseInt(rangeMatch[1], 10);
    const endPanel   = rangeMatch[2] ? parseInt(rangeMatch[2], 10) : startPanel;
    const panelCount = endPanel - startPanel + 1;

    // Segment midpoint and orientation
    const midX = (fromX + toX) / 2;
    const midY = (fromY + toY) / 2;
    const dx = toX - fromX;
    const dy = toY - fromY;
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);

    if (panelCount === 1) {
      // Single panel at segment center
      placements.push({
        panelId:  `cad-panel-${startPanel}`,
        x:        midX / 12,  // Convert inches to feet
        y:        midY / 12,
        rotation: angle,
        angle,
      });
    } else {
      // Distribute panels along the segment
      for (let p = 0; p < panelCount; p++) {
        const t = panelCount === 1 ? 0.5 : (p + 0.5) / panelCount;
        const px = fromX + dx * t;
        const py = fromY + dy * t;
        placements.push({
          panelId:  `cad-panel-${startPanel + p}`,
          x:        px / 12,
          y:        py / 12,
          rotation: angle,
          angle,
        });
      }
    }
  }

  return placements;
}

// ── Main conversion ────────────────────────────────────────────────────────────

export function importCadSite(raw: CadSiteExport): CadSiteImportResult {
  const warnings: string[] = [];
  const now = new Date().toISOString();

  if (raw.sites.length === 0) {
    return {
      site: {
        id:            crypto.randomUUID(),
        projectName:   '',
        drawingNumber: '',
        scale:         1,
        gridSpacing:   0,
        northArrow:    0,
        panels:        [],
        createdAt:     now,
        updatedAt:     now,
      },
      warnings: ['Site export contains no site data.'],
      gridSummary: { xLines: [], yLines: [], wallSegments: [] },
    };
  }

  // Use first site entry (multi-site not supported in cv-web yet)
  const siteEntry = raw.sites[0];
  if (raw.sites.length > 1) {
    warnings.push(`Export contains ${raw.sites.length} sites — only the first site will be imported.`);
  }

  // Parse grid lines
  const xLines = parseGridLines(siteEntry, 'gridXNumeric', 'gridXNumeric_rowCount');
  const yLines = parseGridLines(siteEntry, 'gridYLetter', 'gridYLetter_rowCount');

  // Calculate representative grid spacing (median non-zero spacing)
  const xSpacings = xLines.slice(1).map((l, i) => l.position - xLines[i].position)
    .filter(s => s > 0);
  const ySpacings = yLines.slice(1).map((l, i) => l.position - yLines[i].position)
    .filter(s => s > 0);
  const allSpacings = [...xSpacings, ...ySpacings].sort((a, b) => a - b);
  const medianSpacing = allSpacings.length > 0
    ? allSpacings[Math.floor(allSpacings.length / 2)] / 12  // inches → feet
    : 0;

  // Parse wall lines
  const wallSegments = parseWallLines(siteEntry);

  // Extract panel placements from wall segments
  const placements = extractPanelPlacements(wallSegments, xLines, yLines);

  // Count structural sections that aren't mapped
  const unmappedSections: string[] = [];
  for (const key of ['slabLines', 'slabConnections', 'tiltColumns', 'rebarConnections']) {
    const countKey = `${key}_rowCount`;
    const count = typeof siteEntry[countKey] === 'number' ? siteEntry[countKey] as number : 0;
    if (count > 0) {
      // Check if any non-zero rows exist
      let nonEmpty = 0;
      for (let i = 1; i <= count; i++) {
        const val = siteEntry[`${key}_${i}`];
        if (typeof val === 'string' && !val.match(/^\(\(\s*0\s+0.*\)\)$/)) {
          nonEmpty++;
        }
      }
      if (nonEmpty > 0) {
        unmappedSections.push(`${key} (${nonEmpty} non-empty rows)`);
      }
    }
  }
  if (unmappedSections.length > 0) {
    warnings.push(
      `Structural detail sections not mapped to cv-web: ${unmappedSections.join(', ')}. ` +
      `These contain engineering data (slab edges, rebar, columns) that cv-web does not yet model.`
    );
  }

  // Build summary
  const activeWalls = wallSegments.filter(w => w.active);
  warnings.push(
    `Imported ${xLines.length} X-axis grid lines, ${yLines.length} Y-axis grid lines, ` +
    `${activeWalls.length} active wall segments → ${placements.length} panel placement(s).`
  );

  if (placements.length === 0 && activeWalls.length > 0) {
    warnings.push(
      'Wall segments found but could not resolve panel placements — grid line labels may not match wall line references.'
    );
  }

  const site: SiteData = {
    id:            crypto.randomUUID(),
    projectName:   siteEntry.drawingName?.replace(/\.dwg$/i, '') || '',
    drawingNumber: siteEntry.drawingName?.replace(/\.dwg$/i, '') || '',
    scale:         1,
    gridSpacing:   Math.round(medianSpacing * 100) / 100,  // Round to 2 decimal places
    northArrow:    0,
    panels:        placements,
    createdAt:     now,
    updatedAt:     now,
  };

  return {
    site,
    warnings,
    gridSummary: { xLines, yLines, wallSegments },
  };
}
